<script setup lang="ts">
import IconSvgSpinners180Ring from '~icons/svg-spinners/180-ring'
import IconMaterialSymbolsWarningRounded from '~icons/material-symbols/warning-rounded'
import Icon from '@/shared/components/Icon.vue'
import Dialog from '@/shared/components/Dialog.vue'
import { computed, onBeforeUnmount, ref, watch } from 'vue'

import Button from '@/shared/components/Button.vue'
import { useKeycloak } from '@/shared/composables/useKeycloak'
import { captureLocationForRestore } from '@/shared/utils/postLogoutRedirect'

const DEFAULT_WARNING_THRESHOLD_MS = 2 * 60 * 1000
const TICK_MS = 1000

// Dev-only override: set `localStorage.debug-session-warn-s = '1800'` and reload
// to force the modal to appear when the refresh token has <1800s left. Useful
// for testing the warning UI without sitting idle for ~28 minutes.
const resolveThresholdMs = (): number => {
  if (!import.meta.env.DEV) return DEFAULT_WARNING_THRESHOLD_MS
  const raw = localStorage.getItem('debug-session-warn-s')
  const seconds = raw === null ? NaN : Number(raw)
  if (!Number.isFinite(seconds) || seconds <= 0) return DEFAULT_WARNING_THRESHOLD_MS
  console.warn(`[SessionExpiringDialog] dev override active: threshold=${seconds}s`)
  return seconds * 1000
}

const WARNING_THRESHOLD_MS = resolveThresholdMs()

const {
  isAuthenticated,
  isLoggingOut,
  isVerifyingSession,
  refreshTokenExpiresAt,
  sessionMaxExpiresAt,
  ssoMaxLifespanMs,
  refreshToken,
  logout
} = useKeycloak()

const formatDuration = (ms: number): string => {
  const minutes = Math.round(ms / 60_000)
  if (minutes < 60 || minutes % 60 !== 0) {
    return minutes === 1 ? '1 minute' : `${minutes} minutes`
  }
  const hours = minutes / 60
  return hours === 1 ? '1 hour' : `${hours} hours`
}

// Treat the modal as "max-lifespan reaching" when the SSO max cap is within
// 5 minutes. The cap can't be extended, so the regular "Stay signed in" button
// would just silently fail — better to swap to a different UI that only offers
// "Log out" and explains why.
const APPROACHING_MAX_THRESHOLD_MS = 5 * 60 * 1000

const now = ref(Date.now())
let tickHandle: ReturnType<typeof setInterval> | null = null

const startTicker = (): void => {
  if (tickHandle) return
  tickHandle = setInterval(() => {
    now.value = Date.now()
  }, TICK_MS)
}

const stopTicker = (): void => {
  if (tickHandle) {
    clearInterval(tickHandle)
    tickHandle = null
  }
}

watch(
  isAuthenticated,
  (value) => {
    if (value) startTicker()
    else stopTicker()
  },
  { immediate: true }
)

onBeforeUnmount(stopTicker)

const msRemaining = computed<number | null>(() => {
  if (!isAuthenticated.value || refreshTokenExpiresAt.value === null) return null
  return refreshTokenExpiresAt.value - now.value
})

const visible = computed(() => {
  if (isLoggingOut.value || isVerifyingSession.value) return true
  const ms = msRemaining.value
  return ms !== null && ms > 0 && ms <= WARNING_THRESHOLD_MS
})

const secondsRemaining = computed(() => {
  const ms = msRemaining.value
  if (ms === null) return 0
  return Math.max(0, Math.ceil(ms / 1000))
})

const isApproachingMaxLifespan = computed(() => {
  if (sessionMaxExpiresAt.value === null) return false
  return sessionMaxExpiresAt.value - now.value < APPROACHING_MAX_THRESHOLD_MS
})

const maxLifespanLabel = computed(() => formatDuration(ssoMaxLifespanMs.value))

const isRefreshing = ref(false)

const handleStaySignedIn = async (): Promise<void> => {
  isRefreshing.value = true
  try {
    await refreshToken()
  }
  finally {
    isRefreshing.value = false
  }
}

const handleLogout = async (): Promise<void> => {
  // This dialog only appears because the session is already expiring (or has hit
  // the max-lifespan cap, where "Log out" is the only option). The user didn't
  // choose to leave their work, so remember their location and bring them back
  // after re-login — unlike the deliberate nav-menu logout. (issue #1776)
  captureLocationForRestore()
  await logout()
}
</script>

<template>
  <Dialog
    :model-value="visible"
    :dismissible="false"
    content-class="min-w-[28rem]"
  >
    <template #header>
      <div class="flex items-center gap-2">
        <Icon
          v-if="isLoggingOut || isVerifyingSession"
          class="h-6 w-6"
          severity="primary"
          :icon="IconSvgSpinners180Ring"
        />
        <Icon
          v-else
          class="h-6 w-6"
          severity="warning"
          :icon="IconMaterialSymbolsWarningRounded"
        />
        <span class="font-semibold">
          <template v-if="isLoggingOut">Signing you out</template>
          <template v-else-if="isVerifyingSession">Checking your session</template>
          <template v-else-if="isApproachingMaxLifespan">Daily session limit reached</template>
          <template v-else>Session about to expire</template>
        </span>
      </div>
    </template>

    <template v-if="isLoggingOut">
      <p class="mb-2">Your session has ended. Saving your work and signing you out…</p>
      <p class="text-sm text-surface-500">You will be redirected to the login page in a few seconds.</p>
    </template>
    <template v-else-if="isVerifyingSession">
      <p class="mb-2">Verifying your session with the server…</p>
      <p class="text-sm text-surface-500">If your session has ended you will be signed out automatically.</p>
    </template>
    <template v-else-if="isApproachingMaxLifespan">
      <p class="mb-2">
        You've reached the maximum sign-in time ({{ maxLifespanLabel }}) and will be signed out in
        {{ secondsRemaining }} second<span v-if="secondsRemaining !== 1">s</span>.
      </p>
      <p class="text-sm text-surface-500">
        This session cannot be extended.
      </p>
    </template>
    <template v-else>
      <p class="mb-2">
        Your session will expire in {{ secondsRemaining }} second<span v-if="secondsRemaining !== 1">s</span>.
      </p>
      <p class="text-sm text-surface-500">
        Choose <strong>Stay signed in</strong> to keep working. Unsaved changes may be lost if the session expires.
      </p>
    </template>

    <template
      v-if="!isLoggingOut && !isVerifyingSession"
      #footer
    >
      <Button
        severity="secondary"
        :disabled="isRefreshing"
        label="Log out"
        @click="handleLogout"
      />
      <Button
        v-if="!isApproachingMaxLifespan"
        :disabled="isRefreshing"
        :loading-icon="isRefreshing ? IconSvgSpinners180Ring : undefined"
        label="Stay signed in"
        @click="handleStaySignedIn"
      />
    </template>
  </Dialog>
</template>
